import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { userPrivilegeMapQuery } from '@/queries/iam/user-privilege-map-query';

import { AccessKey, grantTable } from './access/access-table';
import { AccessTable, GrantContext } from './access/types';
import { useAtlasClient } from './use-atlas-client';
import { useCurrentFacility } from './use-current-facility';
import { useUser } from './use-user';

export const usePrivilegeAccessControl = (): UseQueryResult<AccessTable> => {
  const { facilityId } = useCurrentFacility();
  const { userId } = useUser();
  const client = useAtlasClient();

  // Fetch and transform user privileges using TanStack Query's select
  const { data: userPrivilegeMap } = useQuery(
    userPrivilegeMapQuery({
      userId: userId || '',
      client,
    })
  );

  // Simple query key with just the actual dependencies
  return useQuery<AccessTable>({
    queryKey: ['privilegeAccess', userId, facilityId],
    queryFn: async (): Promise<AccessTable> => {
      const context: GrantContext = {
        userId,
        facilityId,
      };

      const access = {} as AccessTable;
      const accessKeys = Object.keys(grantTable) as AccessKey[];

      for (const key of accessKeys) {
        const grant = grantTable[key];
        // Create grant instance with privilege map for context
        const grantWithMap =
          'withPrivilegeMap' in grant ? grant.withPrivilegeMap(userPrivilegeMap!) : grant; // PlaceholderGrant doesn't need privilege map
        access[key] = await grantWithMap.allowed(context);
      }

      return access;
    },
    enabled: !!userPrivilegeMap && !!facilityId && !!userId,
    staleTime: Infinity, // Cache until userId or facilityId changes
  });
};
